/*
  Fig & Bloom — theme-side PostHog storefront commerce events.

  Why this exists
  ---------------
  The PixieHog web pixel runs in Shopify's sandboxed worker. It cannot read the theme's
  posthog-js cookie or localStorage, so it mints its own $session_id for every visit —
  and since it stopped sending $pageview on 30 Jul 2026, every session it creates is a
  zero-pageview fragment. Roughly a third of all "sessions" on the store were artefacts
  of that split, with real orders stranded in them.

  This file re-emits the storefront half of the pixel's event set from the theme, on the
  theme's session id:

    Product Viewed   — product pages, from Liquid (snippets/fb-posthog.liquid), and
                       the quick view modal (sections/product-quick-view.liquid)
    Cart Viewed      — /cart, and a deliberate cart drawer open
    Product Added    — any cart line quantity increase
    Product Removed  — any cart line quantity decrease

  Checkout stays pixel-only: 'Checkout Started', 'Checkout Step Completed',
  'Payment Info Entered' and 'Order Completed' fire on Shopify's checkout domain under
  Checkout Extensibility and genuinely cannot come from here. To let those be attributed
  to the same visit, this file also writes the theme's session and distinct ids onto the
  cart as _ph_session_id / _ph_distinct_id attributes; the pixel reads them back off the
  checkout payload. See docs/posthog-session-fragmentation.md.

  Property schemas
  ----------------
  Payloads are matched field for field against live pixiehog events so downstream
  analysis does not break at the cutover. Two details are load-bearing and look like
  mistakes if you don't know the source:

    * Product Viewed carries the variant title verbatim, so a single-variant product
      reports variant/title/untranslatedTitle as "Default Title". Product Added and
      Product Removed come off the Ajax cart, whose variant_title is null for the same
      product. That asymmetry is what the pixel emits, so it is what we emit.

    * Product Added's `name` is the product title ("Osaka Bouquet"); Product Removed's
      `name` is the cart line title ("Osaka Bouquet - Signature"). Also the pixel's.

  Cart mutations are detected by watching fetch and XMLHttpRequest for the Ajax cart
  endpoints rather than by hooking add-to-cart handlers. There are a dozen add-to-cart
  call sites in this theme (product-form.js, cart-notification.js, delivery-suburb-popup,
  the workshop and gift-card-upsell sections, jQuery in Slider_image and
  custom-subscriptions-buy-buttons) and instrumenting each one would both miss cases and
  rot. The endpoint is the one thing they all have in common.
*/
(function () {
  'use strict';

  var data = window.fbPosthogData;
  if (!data || !window.posthog) return;

  var SNAPSHOT_KEY = 'fb_ph_cart_snapshot';
  var OVERRIDE_KEY = 'fb_ph_storefront';
  var VIEWED_KEY = 'fb_ph_products_viewed';

  /* ------------------------------------------------------------------ *
   * Cutover switch
   *
   * Off by default. The theme setting is flipped on in the same change that
   * disables the five storefront events in the PixieHog app config — ship one
   * without the other and every metric either halves or doubles. ?ph_storefront=1
   * forces it on for the rest of the browser session so a preview theme can be
   * tested before the pixel is touched.
   * ------------------------------------------------------------------ */
  var storefrontOn = (function () {
    var forced = null;
    try {
      var q = new URLSearchParams(window.location.search).get('ph_storefront');
      if (q === '1' || q === '0') sessionStorage.setItem(OVERRIDE_KEY, q);
      forced = sessionStorage.getItem(OVERRIDE_KEY);
    } catch (e) {}
    if (forced === '1') return true;
    if (forced === '0') return false;
    return data.storefrontEvents === true;
  })();

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */
  function cookie(name) {
    var match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]*)');
    return match ? decodeURIComponent(match[2]) : null;
  }

  // The pixel's `client_id` is Shopify's visitor token, which is exactly the value of
  // the _shopify_y cookie — so the theme can reproduce it rather than inventing one.
  function clientId() {
    return cookie('_shopify_y');
  }

  // cart.js reports an absent sku or variant title as '' or null depending on the
  // field; the pixel reports null for both.
  function orNull(value) {
    return value === '' || value === undefined ? null : value;
  }

  function dollars(cents) {
    return typeof cents === 'number' ? Math.round(cents) / 100 : null;
  }

  function round2(value) {
    return Math.round(value * 100) / 100;
  }

  // Captured before this file patches window.fetch below, and used for every request
  // this file makes itself. Our own /cart/update.js is indistinguishable from a real
  // one once it is inside the interceptor, and the only reliable way to tell them apart
  // is not to route ours through it. Suppressing the interceptor for the duration of
  // our request instead would swallow any customer mutation that resolved in the same
  // window — this theme has back-to-back cart calls that land there.
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch : null;

  function internalFetch(url, options) {
    return (nativeFetch || window.fetch).call(window, url, options);
  }

  // Liquid's cart.token and the Ajax cart's token do not always agree on the "?key="
  // suffix, and the pixel reports the bare id.
  function normaliseToken(token) {
    if (!token) return null;
    return String(token).replace(/^gid:\/\/shopify\/Cart\//, '').split('?')[0] || null;
  }

  function capture(event, props) {
    if (!storefrontOn) return;
    var payload = {};
    var client = clientId();
    if (client) payload.client_id = client;
    for (var key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key)) payload[key] = props[key];
    }
    try {
      window.posthog.capture(event, payload);
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Session / distinct id
   *
   * onSessionId is registered on the posthog stub and replayed once the real
   * library loads, so this works even though the snippet above has not finished
   * booting when this file runs.
   * ------------------------------------------------------------------ */
  var currentSessionId = null;
  var seenFirstSessionId = false;

  try {
    window.posthog.onSessionId(function (sessionId) {
      currentSessionId = sessionId;
      if (!seenFirstSessionId) {
        seenFirstSessionId = true;
        return;
      }
      // A new session started while this page stayed open — the usual case being a tab
      // left idle past the 30 minute timeout. posthog-js does not capture a $pageview
      // for it, so the page's late events ($pageleave on unload, a deferred $exception,
      // an $autocapture click) would form a session with no pageview at all. That is
      // the entire $lib=web fragment cluster.
      try {
        window.posthog.capture('$pageview');
      } catch (e) {}
      syncCartAttributes(lastKnownAttributes, lastKnownItemCount);
    });
  } catch (e) {}

  // posthog-js only notices that a session has expired when something asks it for the
  // session id, and the next thing to ask is usually the $pageleave fired during
  // unload — far too late for the handler above to get a $pageview out. Asking on the
  // way back into a backgrounded tab moves that check to a moment when the page is
  // still alive and can actually send one. get_session_id() is what performs the
  // rotation, so this needs no capture of its own: it just lets onSessionId fire early.
  if (typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      try {
        if (window.posthog.__loaded) window.posthog.get_session_id();
      } catch (e) {}
    });
  }

  function sessionId() {
    if (currentSessionId) return currentSessionId;
    try {
      if (window.posthog.__loaded) return window.posthog.get_session_id();
    } catch (e) {}
    return null;
  }

  function distinctId() {
    try {
      if (window.posthog.__loaded) return window.posthog.get_distinct_id();
    } catch (e) {}
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Cart snapshots
   *
   * Product Added / Product Removed are derived by diffing cart line quantities
   * rather than by reading add-to-cart responses, because /cart/update.js and
   * /cart/change.js can add, remove and change lines in one call, and because a
   * quantity bump on the cart page is a Product Added to the pixel too.
   * ------------------------------------------------------------------ */
  function trimLine(item) {
    return {
      id: item.id,
      variant_id: item.variant_id,
      product_id: item.product_id,
      variant_title: item.variant_title,
      product_title: item.product_title,
      title: item.title,
      sku: item.sku,
      price: item.price,
      image: item.image,
      url: item.url
    };
  }

  function snapshotOf(cart) {
    var lines = {};
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      // A cart can hold the same variant on several lines with different line item
      // properties (delivery date, card message), so key on the line key, not the id.
      lines[item.key] = { quantity: item.quantity, item: trimLine(item) };
    }
    return { token: normaliseToken(cart.token), itemCount: cart.item_count, lines: lines };
  }

  function loadSnapshot() {
    try {
      var raw = sessionStorage.getItem(SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveSnapshot(snapshot) {
    try {
      sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch (e) {}
  }

  function lineProps(item, quantity, name) {
    var unitPrice = dollars(item.price);
    return {
      id: String(item.variant_id || item.id),
      product_id: item.product_id == null ? null : String(item.product_id),
      variant: orNull(item.variant_title),
      title: orNull(item.variant_title),
      untranslatedTitle: orNull(item.variant_title),
      name: orNull(name),
      sku: orNull(item.sku),
      price: unitPrice,
      quantity: quantity,
      cost: unitPrice === null ? null : round2(unitPrice * quantity),
      image_url: orNull(item.image),
      url: orNull(item.url)
    };
  }

  // Roll the snapshot's lines up per variant before diffing.
  //
  // The snapshot is keyed on the cart line key, which is the only safe way to hold a
  // cart — the same variant can sit on several lines with different line item
  // properties. But diffing on that key counts a *property* change as a removal plus an
  // addition, because editing a line's properties retires one key and creates another.
  // The delivery-suburb popup rewrites a line's date and message on most orders, so
  // that is not an edge case: over the first 26 hours after cutover it accounted for
  // 112 of 151 'Product Removed' and 138 of 414 'Product Added', pushing both well
  // outside the ±10% the migration had to hold. Shopify's pixel never fired for those,
  // since the merchandise never changed.
  //
  // Netting per variant cancels exactly that churn — same variant, same total quantity,
  // no event — while leaving genuine adds and removes untouched.
  function byVariant(snapshot) {
    var totals = {};
    for (var key in snapshot.lines) {
      if (!Object.prototype.hasOwnProperty.call(snapshot.lines, key)) continue;
      var line = snapshot.lines[key];
      var id = String(line.item.variant_id || line.item.id);
      if (!totals[id]) totals[id] = { quantity: 0, item: line.item };
      totals[id].quantity += line.quantity;
    }
    return totals;
  }

  function diffAndCapture(before, after) {
    var was = byVariant(before);
    var now = byVariant(after);
    var id;
    for (id in now) {
      if (!Object.prototype.hasOwnProperty.call(now, id)) continue;
      var previouslyHeld = was[id] ? was[id].quantity : 0;
      var added = now[id].quantity - previouslyHeld;
      if (added > 0) capture('Product Added', lineProps(now[id].item, added, now[id].item.product_title));
    }
    for (id in was) {
      if (!Object.prototype.hasOwnProperty.call(was, id)) continue;
      var stillHeld = now[id] ? now[id].quantity : 0;
      var removed = was[id].quantity - stillHeld;
      // Product Removed reports the cart line title, which already includes the variant.
      if (removed > 0) capture('Product Removed', lineProps(was[id].item, removed, was[id].item.title));
    }
  }

  /* ------------------------------------------------------------------ *
   * Checkout stitching
   *
   * Checkout events still arrive from the sandboxed pixel with a session id of
   * their own. Carrying the theme's ids across as cart attributes survives the
   * domain hop to checkout, which reading the posthog cookie from the pixel
   * sandbox does not reliably do.
   * ------------------------------------------------------------------ */
  var lastKnownAttributes = (data.cart && data.cart.attributes) || {};
  var lastKnownItemCount = (data.cart && data.cart.itemCount) || 0;
  var lastKnownToken = null;

  function syncCartAttributes(attributes, itemCount) {
    if (!itemCount) return; // Nothing to attribute an empty cart to.
    var session = sessionId();
    var distinct = distinctId();
    if (!session || !distinct) return;
    var current = attributes || {};
    if (current._ph_session_id === session && current._ph_distinct_id === distinct) return;

    // internalFetch, so the interceptor never sees this write and never has to be
    // switched off to hide it.
    internalFetch(root() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // /cart/update.js merges attributes: keys not named here are left alone.
      body: JSON.stringify({ attributes: { _ph_session_id: session, _ph_distinct_id: distinct } })
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (cart) lastKnownAttributes = cart.attributes || {};
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------ *
   * Reconciliation
   * ------------------------------------------------------------------ */
  function root() {
    var value = data.rootUrl || '/';
    return value.charAt(value.length - 1) === '/' ? value : value + '/';
  }

  function fetchCart() {
    return internalFetch(root() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function applyCart(cart, options) {
    var next = snapshotOf(cart);
    var previous = loadSnapshot();
    var comparable =
      previous && (!options || !options.requireSameToken || previous.token === next.token);
    if (comparable) diffAndCapture(previous, next);
    saveSnapshot(next);
    lastKnownAttributes = cart.attributes || {};
    lastKnownItemCount = cart.item_count || 0;
    lastKnownToken = next.token;
    syncCartAttributes(lastKnownAttributes, lastKnownItemCount);
  }

  // Cart mutations can overlap (add-to-cart plus the delivery popup rewriting line item
  // properties). Serialising the reads keeps two in-flight diffs from double-counting.
  var queue = Promise.resolve();
  function reconcile(options) {
    queue = queue.then(function () {
      return fetchCart().then(function (cart) {
        if (cart) applyCart(cart, options);
      });
    });
    return queue;
  }

  // Cart Viewed needs a token, and Liquid cannot supply one (see the Cart Viewed
  // section below). Joins the same queue so it settles behind whatever read is already
  // in flight — usually the page load's — rather than racing a second one.
  function ensureCartLoaded() {
    queue = queue.then(function () {
      if (lastKnownToken) return null;
      return fetchCart().then(function (cart) {
        if (cart) applyCart(cart, { requireSameToken: true });
      });
    });
    return queue;
  }

  /* ------------------------------------------------------------------ *
   * Ajax cart interception
   * ------------------------------------------------------------------ */
  function isCartMutation(url) {
    if (!url) return false;
    var path;
    try {
      path = new URL(String(url), window.location.origin).pathname;
    } catch (e) {
      path = String(url);
    }
    return /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
  }

  function onCartMutation() {
    reconcile();
  }

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var watched = isCartMutation(url);
      var result = nativeFetch.apply(this, arguments);
      if (!watched) return result;
      return result.then(function (response) {
        if (response && response.ok) onCartMutation();
        return response;
      });
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__fbPhCartMutation = isCartMutation(url);
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr.__fbPhCartMutation) {
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) onCartMutation();
      });
    }
    return nativeSend.apply(this, arguments);
  };

  /* ------------------------------------------------------------------ *
   * Page load
   * ------------------------------------------------------------------ */
  var stored = loadSnapshot();

  if (!stored) {
    // First page of the tab. Seed without emitting: the deltas that got the cart to
    // this state either happened in another tab or belong to a visit we were not
    // watching, and inventing events for them would inflate counts. reconcile() with
    // no stored snapshot cannot diff, so it saves and stays quiet.
    if (lastKnownItemCount > 0) {
      reconcile();
    } else {
      saveSnapshot({ token: null, itemCount: 0, lines: {} });
    }
  } else if (stored.itemCount !== lastKnownItemCount) {
    // The cart moved without us seeing the request — a plain (non-Ajax) form POST to
    // /cart/add, a mutation in another tab, or a back/forward into a stale page.
    // Liquid's cart.item_count is the trigger because cart.token is not available to
    // compare against; requireSameToken still keeps a *replaced* cart (checkout
    // completed, cart cleared server-side) from being diffed line by line as though
    // the shopper had just emptied it by hand.
    reconcile({ requireSameToken: true });
  } else {
    syncCartAttributes(lastKnownAttributes, lastKnownItemCount);
  }

  /* ------------------------------------------------------------------ *
   * Product Viewed
   *
   * Fires from two surfaces now. The product page payload comes from Liquid in
   * snippets/fb-posthog.liquid; the quick view payload comes from the modal's own
   * markup. Both go through here so the two are impossible to drift apart.
   * ------------------------------------------------------------------ */

  // A shopper who opens a card's quick view and then clicks through to the full
  // product page would otherwise be counted twice for one product. Deduplicating
  // per product costs about 7% of Product Viewed volume on this store (measured
  // against pre-cutover pixiehog events), so the per-pageview ratio the migration
  // is judged on stays comparable while the double counting does not happen.
  //
  // sessionStorage rather than localStorage: quick view and the product page are the
  // same tab, which is the case worth collapsing, and a per-tab record cannot leak a
  // suppression into a genuinely separate visit. The PostHog session id is stored
  // alongside it so a tab left open past the 30 minute session timeout starts over
  // instead of suppressing the shopper's whole return visit.
  function alreadyViewed(productId) {
    if (productId === null || productId === undefined) return false;
    var key = String(productId);
    var session = sessionId();
    var record = null;
    try {
      record = JSON.parse(sessionStorage.getItem(VIEWED_KEY) || 'null');
    } catch (e) {}
    if (!record || !record.ids || (session && record.session && record.session !== session)) {
      record = { session: session, ids: {} };
    }
    if (session && !record.session) record.session = session;
    if (record.ids[key]) return true;
    record.ids[key] = 1;
    try {
      sessionStorage.setItem(VIEWED_KEY, JSON.stringify(record));
    } catch (e) {}
    return false;
  }

  function captureProductViewed(product) {
    if (!product || product.productId === null || product.productId === undefined) return;
    if (alreadyViewed(product.productId)) return;
    var price = dollars(product.priceCents);
    capture('Product Viewed', {
      id: String(product.variantId),
      product_id: String(product.productId),
      // Verbatim from Liquid, so single-variant products report "Default Title" — see
      // the property schema note at the top of this file.
      variant: orNull(product.variantTitle),
      title: orNull(product.variantTitle),
      untranslatedTitle: orNull(product.variantTitle),
      name: orNull(product.title),
      sku: orNull(product.sku),
      price: price,
      currency: orNull(data.currency),
      value: price,
      quantity: 1,
      image_url: orNull(product.imageUrl),
      url: orNull(product.url)
    });
  }

  if (data.product) captureProductViewed(data.product);

  /* ------------------------------------------------------------------ *
   * Quick view
   *
   * Every quick view entry point in the theme — the collection and search grids, the
   * product carousel, lookbook, bundles, events, recently viewed and complementary
   * cards — renders snippets/quick-view.liquid, which is a <quick-view-modal>. That
   * element fetches /products/<handle>?view=quickview and assigns the response to
   * #quickViewWrapper.innerHTML (assets/quick_view_modal.js). Watching the one wrapper
   * covers all of them without instrumenting a dozen card snippets, the same reasoning
   * as the Ajax cart interception above, and the same thing
   * assets/fb-variant-location-filter.js does with this wrapper.
   *
   * Quick add needs nothing here: the modal's add-to-cart goes through
   * assets/product-form.js, which posts to /cart/add.js and so is already counted by
   * the interceptor above.
   * ------------------------------------------------------------------ */
  function quickViewProduct(wrapper) {
    var node = wrapper.querySelector('[data-fb-posthog-quickview]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  (function watchQuickView() {
    if (typeof MutationObserver !== 'function' || !document.getElementById) return;
    var wrapper = document.getElementById('quickViewWrapper');
    if (!wrapper) return;
    // One innerHTML assignment produces several mutation records; the product id is
    // what tells an actual modal opening from the rest of them.
    var showing = null;
    new MutationObserver(function () {
      var product = quickViewProduct(wrapper);
      if (!product || product.productId === showing) return;
      showing = product.productId;
      captureProductViewed(product);
    }).observe(wrapper, { childList: true, subtree: true });
  })();

  /* ------------------------------------------------------------------ *
   * Cart Viewed
   *
   * The token comes from the Ajax cart because Liquid cannot supply one — see the
   * comment in snippets/fb-posthog.liquid. The first version of this event read
   * Liquid's cart.token, which is always null, and so never fired: PostHog recorded
   * zero Cart Viewed against 48 /cart pageviews in the six hours after the cutover.
   *
   * `surface` is the one property the pixel did not send. The pixel only ever fired
   * Cart Viewed on the /cart page — 202 events over the 30 hours before the cutover,
   * every one of them on that path and none from the drawer — so splitting the two
   * keeps the historical series comparable now that the drawer is counted as well.
   * ------------------------------------------------------------------ */
  function captureCartViewed(surface) {
    if (!storefrontOn) return;
    ensureCartLoaded().then(function () {
      if (!lastKnownToken || !lastKnownItemCount) return;
      capture('Cart Viewed', {
        id: lastKnownToken,
        cart_id: lastKnownToken,
        url: window.location.href,
        totalQuantity: lastKnownItemCount,
        surface: surface
      });
    });
  }

  if (data.isCartPage && lastKnownItemCount > 0) captureCartViewed('page');

  // The header cart icon is wrapped in <open-minicart>, whose handler preventDefaults
  // the link and slides the drawer out (assets/cart-notification.js). Delegating from
  // the document covers both header instances, and survives the header being
  // re-rendered by the section rendering API.
  //
  // The drawer that opens by itself after an add to cart is deliberately not counted.
  // That moment is already a Product Added, and counting it would put Cart Viewed
  // far above the volume the pixel used to report rather than restoring it.
  if (document.addEventListener) {
    document.addEventListener('click', function (event) {
      var target = event && event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest('open-minicart')) return;
      captureCartViewed('drawer');
    });
  }

})();
